import { useState, useRef, useEffect } from 'react';
import { VoiceRecorder } from '@independo/capacitor-voice-recorder';
import { createTranscriptionFromBase64 } from '@/api/transcriptions';
import { aacAdtsToMp4Base64 } from '@/utils/audio/aacAdtsToMp4';
import type { UseVoiceRecordOptions, UseVoiceRecordReturn } from './useVoiceRecord';

export const useVoiceRecordNative = ({
  onTranscription,
  onError,
  onDictationAttempted,
}: UseVoiceRecordOptions): UseVoiceRecordReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const isActiveRef = useRef(false);

  useEffect(() => {
    isActiveRef.current = isRecording;
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (isActiveRef.current) {
        VoiceRecorder.stopRecording().catch(() => {});
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const { value: hasPermission } = await VoiceRecorder.hasAudioRecordingPermission();
      if (!hasPermission) {
        const { value: granted } = await VoiceRecorder.requestAudioRecordingPermission();
        // Permission was not held — we had to request it; granted reflects user's choice
        onDictationAttempted?.({ permissionRequested: true, permissionGranted: granted });
        if (!granted) {
          onError?.('Microphone access is needed to record');
          return;
        }
      } else {
        // Permission was already granted — no prompt shown
        onDictationAttempted?.({ permissionRequested: false, permissionGranted: true });
      }
      await VoiceRecorder.startRecording();
      setIsRecording(true);
    } catch {
      // Native API threw before we could start — treat as denied without a prompt
      onDictationAttempted?.({ permissionRequested: true, permissionGranted: false });
      onError?.('Microphone access is needed to record');
    }
  };

  const stopAndTranscribe = async (upload: boolean) => {
    setIsRecording(false);
    try {
      const { value } = await VoiceRecorder.stopRecording();
      if (!upload) return;

      const { recordDataBase64, fileExtension } = value;

      if (!recordDataBase64) {
        onError?.('No audio recorded');
        return;
      }

      // Android returns raw AAC ADTS (.aac), which Whisper rejects. Remux it
      // into an MP4 container in-place; iOS already produces .m4a natively.
      const isAdts = (fileExtension || '').toLowerCase() === 'aac';
      const payload = isAdts ? aacAdtsToMp4Base64(recordDataBase64) : recordDataBase64;
      const ext = isAdts ? 'm4a' : fileExtension || 'm4a';

      setIsTranscribing(true);
      try {
        const { text, fileId } = await createTranscriptionFromBase64(payload, `recording.${ext}`);
        const trimmed = text.trim();
        if (trimmed) onTranscription(trimmed, fileId);
      } catch (err) {
        onError?.(err instanceof Error ? err.message : 'Transcription failed');
      } finally {
        setIsTranscribing(false);
      }
    } catch (err) {
      setIsTranscribing(false);
      onError?.(err instanceof Error ? err.message : 'Recording failed');
    }
  };

  const toggleRecording = () => {
    if (isTranscribing) return;
    if (isRecording) {
      stopAndTranscribe(true);
      return;
    }
    startRecording();
  };

  const stopRecording = (cancel = false) => {
    if (!isRecording) return;
    stopAndTranscribe(!cancel);
  };

  return { isRecording, isTranscribing, toggleRecording, stopRecording };
};
